import { isAxiosError } from 'axios'
import { useState } from 'react'

import api from '@/lib/api'
import type { InvoiceData } from '@/lib/api.types'

export interface CompanyVatFill {
  name?: string
  street?: string
  houseNumber?: string
  postalCode?: string
  city?: string
}

export interface VatLookupFeedback {
  type: 'error' | 'warning' | 'info'
  messageKey: string
}

interface UseCompanyVatLookupArgs {
  onFill: (fields: CompanyVatFill) => void
}

const hasParsedAddress = (data: InvoiceData) => {
  const address = data.address
  return Boolean(address && (address.city || address.street || address.postalCode || address.houseNumber))
}

const mapInvoiceDataToFill = (data: InvoiceData): CompanyVatFill => {
  const fill: CompanyVatFill = {}

  if (data.name) {
    fill.name = data.name
  }

  const address = data.address
  if (address && hasParsedAddress(data)) {
    if (address.street) fill.street = address.street
    if (address.houseNumber) fill.houseNumber = address.houseNumber
    if (address.postalCode) fill.postalCode = address.postalCode
    if (address.city) fill.city = address.city
  } else if (data.addressRaw) {
    fill.street = data.addressRaw.replace(/\n+/g, ' ').trim()
  }

  return fill
}

const getErrorMessageKey = (error: unknown) => {
  const status = isAxiosError(error) ? error.response?.status : undefined

  switch (status) {
    case 400:
      return 'vatLookupInvalidFormat'
    case 429:
      return 'vatLookupRateLimit'
    case 503:
      return 'vatLookupUnavailable'
  }

  // W trybie WEBVIEW błędy z mostka przychodzą jako Error("[KOD] wiadomość") — patrz androidBridge.ts.
  if (error instanceof Error) {
    if (error.message.startsWith('[VALIDATION_ERROR]')) {
      return 'vatLookupInvalidFormat'
    }
    if (error.message.startsWith('[NETWORK_ERROR]') || error.message.startsWith('[SERVER_ERROR]')) {
      return 'vatLookupUnavailable'
    }
  }

  return 'vatLookupGenericError'
}

const useCompanyVatLookup = ({ onFill }: UseCompanyVatLookupArgs) => {
  const [isLoading, setIsLoading] = useState(false)
  const [feedback, setFeedback] = useState<VatLookupFeedback | null>(null)

  const lookup = async (vatId: string) => {
    if (!vatId || isLoading) {
      return
    }

    setIsLoading(true)
    setFeedback(null)

    try {
      const data = await api.getInvoiceData(vatId)

      if (!data.valid) {
        setFeedback({ type: 'warning', messageKey: 'vatLookupNotFound' })
        return
      }

      // Numer poprawny, ale kraj nie udostępnia danych w VIES (m.in. DE i ES) — nie ma czego uzupełnić.
      if (!data.name && !hasParsedAddress(data) && !data.addressRaw) {
        setFeedback({ type: 'info', messageKey: 'vatLookupPrivacy' })
        return
      }

      onFill(mapInvoiceDataToFill(data))

      if (!hasParsedAddress(data) && data.addressRaw) {
        setFeedback({ type: 'info', messageKey: 'vatLookupAddressRaw' })
      }
    } catch (error) {
      setFeedback({ type: 'error', messageKey: getErrorMessageKey(error) })
    } finally {
      setIsLoading(false)
    }
  }

  const clearFeedback = () => setFeedback(null)

  return { lookup, isLoading, feedback, clearFeedback }
}

export default useCompanyVatLookup
